import { useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle, BookOpen, Pencil, Languages, Brain, HelpCircle, Flame,
  Send, Users, Trash2, Reply, X, Hash, Circle
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useChatSocket } from "../hooks/useChatSocket";
import Header from "../components/Header";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const ICONS = { MessageCircle, BookOpen, Pencil, Languages, Brain, HelpCircle, Flame };

function formatTime(iso) {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) {
    return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function Avatar({ name, role }) {
  const initial = (name || "?")[0].toUpperCase();
  return (
    <div className={`h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-display font-extrabold text-xs ${
      role === "admin" ? "bg-gradient-to-br from-gcgold to-gcgold-dark text-ink border border-gcgold-light/50"
      : "bg-gradient-to-br from-gcgreen to-gcgreen-dark text-gcgold border border-gcgold/20"
    }`}>
      {initial}
    </div>
  );
}

function MessageItem({ msg, currentUser, onReply, onDelete, replyTarget }) {
  const isAdmin = msg.user_role === "admin";
  const isMine = currentUser?.id === msg.user_id;
  return (
    <div className="group flex gap-3 px-4 py-2.5 hover:bg-white/[0.02] rounded-xl transition-colors" data-testid={`message-${msg.id}`}>
      <Avatar name={msg.user_name} role={msg.user_role} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`font-display font-bold text-sm ${isAdmin ? "text-gcgold" : isMine ? "text-emerald-300" : "text-white"}`}>
            {msg.user_name}
          </span>
          {isAdmin && <span className="text-[9px] uppercase tracking-[0.22em] font-mono font-bold text-gcgold bg-gcgold/15 px-1.5 py-0.5 rounded">Admin</span>}
          <span className="text-[10px] text-slate-600 font-mono">{formatTime(msg.created_at)}</span>
        </div>
        {replyTarget && (
          <div className="mt-1 mb-1.5 pl-3 border-l-2 border-gcgold/40 text-xs text-slate-500 line-clamp-1">
            <span className="text-gcgold font-bold">↪ {replyTarget.user_name}:</span> {replyTarget.content}
          </div>
        )}
        <div className="text-sm text-slate-200 leading-relaxed break-words">{msg.content}</div>
      </div>
      <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onReply(msg)}
          className="p-1.5 rounded-lg hover:bg-white/5 text-slate-500 hover:text-gcgold"
          title="Responder"
          data-testid={`reply-${msg.id}`}
        >
          <Reply className="h-3.5 w-3.5" />
        </button>
        {currentUser?.role === "admin" && (
          <button
            onClick={() => onDelete(msg)}
            className="p-1.5 rounded-lg hover:bg-red-950/40 text-slate-500 hover:text-red-400"
            title="Eliminar"
            data-testid={`delete-${msg.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [currentChannel, setCurrentChannel] = useState("general");
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState([]);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [typingUsers, setTypingUsers] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const scrollRef = useRef(null);
  const typingTimerRef = useRef(null);

  // Load channels
  useEffect(() => {
    api.get("/chat/channels").then((r) => setChannels(r.data)).catch(console.error);
  }, []);

  // Load messages on channel change
  useEffect(() => {
    if (!currentChannel) return;
    api.get(`/chat/channels/${currentChannel}/messages?limit=100`).then((r) => setMessages(r.data)).catch(console.error);
    setReplyTo(null);
  }, [currentChannel]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Cleanup typing indicators
  useEffect(() => {
    const i = setInterval(() => {
      const now = Date.now();
      setTypingUsers((prev) => {
        const next = {};
        Object.entries(prev).forEach(([k, v]) => { if (now - v.at < 3500) next[k] = v; });
        return next;
      });
    }, 1500);
    return () => clearInterval(i);
  }, []);

  // WebSocket
  const handleEvent = useMemo(() => (data) => {
    if (data.type === "presence") {
      setOnline(data.users || []);
    } else if (data.type === "message") {
      const m = data.message;
      if (m.channel_id === currentChannel) {
        setMessages((prev) => [...prev, m]);
      } else {
        // notify on other channels (could show badge — kept simple)
      }
    } else if (data.type === "deleted") {
      setMessages((prev) => prev.filter((m) => m.id !== data.message_id));
    } else if (data.type === "typing") {
      if (data.user_id !== user?.id && data.channel_id === currentChannel) {
        setTypingUsers((prev) => ({ ...prev, [data.user_id]: { name: data.user_name, at: Date.now() } }));
      }
    } else if (data.type === "error") {
      toast.error(data.message);
    }
  }, [currentChannel, user?.id]);

  const { status, send } = useChatSocket(handleEvent, !!user);

  const sendMessage = () => {
    const content = draft.trim();
    if (!content) return;
    send({ type: "message", channel_id: currentChannel, content, reply_to: replyTo?.id || null });
    setDraft("");
    setReplyTo(null);
  };

  const onType = () => {
    if (typingTimerRef.current) return;
    send({ type: "typing", channel_id: currentChannel });
    typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 2000);
  };

  const deleteMessage = async (msg) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    try {
      await api.delete(`/admin/chat/messages/${msg.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Build a reply lookup map
  const msgMap = useMemo(() => Object.fromEntries(messages.map((m) => [m.id, m])), [messages]);
  const activeChannel = channels.find((c) => c.id === currentChannel);
  const typingArr = Object.values(typingUsers);

  return (
    <div className="min-h-screen bg-ink relative flex flex-col">
      <SEO title="Comunidad" path="/comunidad" />
      <div className="fixed inset-0 bg-aurora pointer-events-none" />

      <div className="relative">
        <Header />
      </div>

      <div className="relative flex-1 flex max-w-[1600px] w-full mx-auto px-2 sm:px-4 lg:px-8 py-4 gap-4 overflow-hidden" style={{ minHeight: "calc(100vh - 64px)" }}>
        {/* Sidebar: channels */}
        <aside className={`${sidebarOpen ? "fixed inset-y-0 left-0 z-30 w-72 p-4" : "hidden"} md:relative md:flex md:w-60 lg:w-64 flex-shrink-0 md:p-0 flex flex-col gap-4`} data-testid="chat-sidebar">
          {sidebarOpen && (
            <button onClick={() => setSidebarOpen(false)} className="md:hidden absolute top-2 right-2 h-8 w-8 flex items-center justify-center rounded-lg bg-white/5">
              <X className="h-4 w-4 text-slate-300" />
            </button>
          )}
          <div className={`${sidebarOpen ? "h-full" : ""} bg-ink-800 border border-white/5 rounded-2xl p-4`}>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono flex items-center gap-2">
              <Hash className="h-3 w-3" /> Canales
            </div>
            <div className="space-y-1">
              {channels.map((c) => {
                const Icon = ICONS[c.icon] || MessageCircle;
                const active = c.id === currentChannel;
                return (
                  <button
                    key={c.id}
                    onClick={() => { setCurrentChannel(c.id); setSidebarOpen(false); }}
                    className={`flex items-center gap-2.5 w-full text-left px-3 py-2 rounded-xl transition-colors text-sm ${
                      active ? "bg-gcgreen/20 text-white border border-gcgreen/30" : "text-slate-400 hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={`channel-${c.id}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="font-medium truncate">{c.name}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-gcgold mb-3 font-mono flex items-center gap-2">
                <Users className="h-3 w-3" /> En línea
                <span className="ml-auto text-slate-500">{online.length}</span>
              </div>
              <div className="space-y-1 max-h-60 overflow-auto pr-1">
                {online.length === 0 && <div className="text-xs text-slate-600">Nadie por aquí.</div>}
                {online.map((u) => (
                  <div key={u.id} className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-400" data-testid={`online-${u.id}`}>
                    <Circle className="h-2 w-2 fill-emerald-400 text-emerald-400" />
                    <span className="truncate">{u.name}</span>
                    {u.role === "admin" && <span className="text-[8px] uppercase tracking-wider font-mono font-bold text-gcgold">ADM</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Main chat area */}
        <main className="flex-1 flex flex-col bg-ink-800 border border-white/5 rounded-2xl overflow-hidden min-w-0">
          {/* Channel header */}
          <div className="flex-shrink-0 px-5 py-4 border-b border-white/5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setSidebarOpen(true)} className="md:hidden h-8 w-8 flex items-center justify-center rounded-lg bg-white/5 text-slate-300 flex-shrink-0">
                <Hash className="h-4 w-4" />
              </button>
              <Hash className="h-5 w-5 text-gcgold flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-display font-extrabold text-white truncate" data-testid="active-channel-name">{activeChannel?.name}</div>
                <div className="text-xs text-slate-500 truncate">{activeChannel?.description}</div>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider">
              <span className={`h-1.5 w-1.5 rounded-full ${status === "connected" ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} />
              <span className="text-slate-500">{status === "connected" ? "Conectado" : status === "connecting" ? "Conectando..." : "Desconectado"}</span>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto py-3" data-testid="messages-list">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center px-8 py-16">
                <Hash className="h-10 w-10 text-slate-700 mb-3" />
                <div className="font-display text-xl font-bold text-white mb-1">Bienvenido a #{activeChannel?.name}</div>
                <div className="text-sm text-slate-500">Sé el primero en romper el hielo.</div>
              </div>
            )}
            {messages.map((m) => (
              <MessageItem
                key={m.id}
                msg={m}
                currentUser={user}
                onReply={setReplyTo}
                onDelete={deleteMessage}
                replyTarget={m.reply_to ? msgMap[m.reply_to] : null}
              />
            ))}
            {typingArr.length > 0 && (
              <div className="px-4 py-2 text-xs text-slate-500 italic">
                {typingArr.map((t) => t.name).join(", ")} {typingArr.length === 1 ? "está" : "están"} escribiendo...
              </div>
            )}
          </div>

          {/* Reply preview */}
          {replyTo && (
            <div className="flex-shrink-0 px-5 py-2 border-t border-white/5 bg-white/[0.02] flex items-center justify-between gap-2">
              <div className="text-xs text-slate-400 truncate">
                Respondiendo a <span className="text-gcgold font-bold">{replyTo.user_name}</span>: <span className="text-slate-500">{replyTo.content}</span>
              </div>
              <button onClick={() => setReplyTo(null)} className="text-slate-500 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Composer */}
          <div className="flex-shrink-0 p-4 border-t border-white/5">
            <div className="flex gap-2">
              <input
                type="text"
                value={draft}
                onChange={(e) => { setDraft(e.target.value); onType(); }}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder={`Mensaje en #${activeChannel?.name || "general"}`}
                maxLength={1000}
                className="flex-1 bg-white/5 border border-white/10 text-white placeholder:text-slate-600 rounded-xl px-4 h-11 focus:border-gcgold/40 focus:outline-none focus:ring-2 focus:ring-gcgold/20"
                data-testid="chat-input"
                disabled={status !== "connected"}
              />
              <Button
                onClick={sendMessage}
                disabled={!draft.trim() || status !== "connected"}
                className="bg-gcgold hover:bg-gcgold-light text-ink font-bold h-11 px-5 rounded-xl"
                data-testid="chat-send-button"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-1.5 text-[10px] text-slate-600 font-mono">
              Enter = enviar · Shift+Enter = nueva línea · {draft.length}/1000
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
